import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { devLog } from '@/utils/logger';
import { isStandalone, STANDALONE_DISPLAY_MODES } from '@/utils/platform';

/**
 * BeforeInstallPromptEvent interface
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UseInstallPromptReturn {
  isInstallable: boolean;
  isInstalled: boolean;
  canShowPrompt: boolean;
  /** True while the 7-day dismiss snooze (`pwa-install-dismissed`) is active. */
  isDismissed: boolean;
  /**
   * True once the anti-annoyance engagement heuristic is satisfied
   * (30s on-page dwell OR this is the visitor's 2nd+ visit). Reactive —
   * updates without a remount. Intended for consumers that gate a UI
   * (e.g. the iOS manual-install fallback) that has no native
   * `beforeinstallprompt` signal to key off of.
   */
  isEngaged: boolean;
  promptInstall: () => Promise<void>;
  dismissPrompt: () => void;
}

const STORAGE_KEYS = {
  DISMISSED: 'pwa-install-dismissed',
  VISIT_COUNT: 'pwa-visit-count',
} as const;

const CONFIG = {
  ENGAGEMENT_THRESHOLD_MS: 30_000,
  DISMISS_DURATION_MS: 7 * 24 * 60 * 60 * 1000,
  MIN_VISIT_COUNT: 2,
} as const;

const getDismissTimestamp = (): number | null => {
  try {
    const dismissed = localStorage.getItem(STORAGE_KEYS.DISMISSED);
    if (!dismissed) return null;
    const parsed = parseInt(dismissed, 10);
    return isNaN(parsed) ? null : parsed;
  } catch (error) {
    devLog.warn('[PWA] localStorage read failed:', error);
    return null;
  }
};

const isDismissPeriodActive = (): boolean => {
  const dismissedAt = getDismissTimestamp();
  if (!dismissedAt) return false;
  return (Date.now() - dismissedAt) < CONFIG.DISMISS_DURATION_MS;
};

const getVisitCount = (): number => {
  try {
    const count = localStorage.getItem(STORAGE_KEYS.VISIT_COUNT);
    if (!count) return 0;
    const parsed = parseInt(count, 10);
    return isNaN(parsed) ? 0 : parsed;
  } catch (error) {
    devLog.warn('[PWA] localStorage read failed:', error);
    return 0;
  }
};

const incrementVisitCount = (): number => {
  try {
    const newCount = getVisitCount() + 1;
    localStorage.setItem(STORAGE_KEYS.VISIT_COUNT, newCount.toString());
    return newCount;
  } catch (error) {
    devLog.warn('[PWA] localStorage write failed:', error);
    return 0;
  }
};

/**
 * Module-level singleton state.
 *
 * The `beforeinstallprompt` event fires exactly once per page load, often
 * before any consumer component mounts. We register the listener at module
 * load and cache the event so any number of `useInstallPrompt()` consumers
 * (banner + settings card + …) all see the same captured prompt.
 */
let cachedPrompt: BeforeInstallPromptEvent | null = null;
// Latch set once by `appinstalled` (user is still in the browser tab, where
// isStandalone() is false) and never cleared, so rechecks can't undo it.
let installedViaEvent = false;
const computeInstalled = (): boolean => installedViaEvent || isStandalone();
let cachedInstalled = computeInstalled();
const subscribers = new Set<() => void>();
let visitTrackedThisLoad = false;

const notify = (): void => {
  subscribers.forEach((fn) => fn());
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    cachedPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    installedViaEvent = true;
    cachedInstalled = true;
    try {
      localStorage.removeItem(STORAGE_KEYS.DISMISSED);
      localStorage.removeItem(STORAGE_KEYS.VISIT_COUNT);
    } catch (error) {
      devLog.warn('[PWA] localStorage cleanup failed:', error);
    }
    notify();
  });

  // Re-evaluate installed state. `appinstalled` only fires at install time,
  // not on later launches, and some browsers apply `display-mode` after the
  // initial script eval. Only notify on an actual change.
  const recheckInstalled = (): void => {
    const next = computeInstalled();
    if (next !== cachedInstalled) {
      cachedInstalled = next;
      notify();
    }
  };

  // Runs at module load: must never throw (old iOS Safari <14 / old Android
  // lack MediaQueryList.addEventListener). visibilitychange/focus below cover
  // the case where no media-query listener can be attached at all.
  const addDisplayModeListener = (mode: string, fn: () => void): void => {
    try {
      const mql = window.matchMedia(`(display-mode: ${mode})`);
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', fn);
      } else if (typeof mql.addListener === 'function') {
        // Legacy Safari <14 / old Android
        mql.addListener(fn);
      }
    } catch (error) {
      devLog.warn('[PWA] display-mode listener attach failed:', error);
    }
  };

  STANDALONE_DISPLAY_MODES.forEach((mode) => addDisplayModeListener(mode, recheckInstalled));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recheckInstalled();
  });
  window.addEventListener('focus', recheckInstalled);
}

const subscribe = (fn: () => void): (() => void) => {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
};

const getPromptSnapshot = (): BeforeInstallPromptEvent | null => cachedPrompt;
const getInstalledSnapshot = (): boolean => cachedInstalled;

/**
 * Custom hook for managing PWA install prompt.
 *
 * Shared state across all consumers (module-level singleton) so a deferred
 * `beforeinstallprompt` captured during app boot is available to a Settings
 * card mounted minutes later.
 */
export const useInstallPrompt = (): UseInstallPromptReturn => {
  const deferredPrompt = useSyncExternalStore(subscribe, getPromptSnapshot, getPromptSnapshot);
  const isInstalled = useSyncExternalStore(subscribe, getInstalledSnapshot, getInstalledSnapshot);

  const [isDismissed, setIsDismissed] = useState<boolean>(isDismissPeriodActive());
  const [hasMetEngagementThreshold, setHasMetEngagementThreshold] = useState<boolean>(false);
  const [visitCount, setVisitCount] = useState<number>(getVisitCount());

  const isInstallable = deferredPrompt !== null;

  const canShowPrompt =
    isInstallable &&
    !isInstalled &&
    !isDismissed &&
    (hasMetEngagementThreshold || getVisitCount() >= CONFIG.MIN_VISIT_COUNT);

  // Same heuristic as `canShowPrompt`'s engagement gate, but exposed on its
  // own (independent of installability) and driven entirely by state so it
  // re-renders consumers the moment either condition flips.
  const isEngaged = hasMetEngagementThreshold || visitCount >= CONFIG.MIN_VISIT_COUNT;

  const dismissPrompt = useCallback((): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.DISMISSED, Date.now().toString());
    } catch (error) {
      devLog.warn('[PWA] localStorage write failed:', error);
    }
    setIsDismissed(true);
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    if (!cachedPrompt) {
      devLog.warn('Install prompt not available');
      return;
    }
    try {
      await cachedPrompt.prompt();
      const { outcome } = await cachedPrompt.userChoice;
      if (outcome === 'accepted') {
        devLog.log('User accepted the install prompt');
      } else {
        devLog.log('User dismissed the install prompt');
        dismissPrompt();
      }
      cachedPrompt = null;
      notify();
    } catch (error) {
      devLog.log('Error showing install prompt:', error);
    }
  }, [dismissPrompt]);

  useEffect(() => {
    // Track visit once per page load (not once per hook instance)
    if (!visitTrackedThisLoad) {
      visitTrackedThisLoad = true;
      setVisitCount(incrementVisitCount());
    }

    const engagementTimer = setTimeout(() => {
      setHasMetEngagementThreshold(true);
    }, CONFIG.ENGAGEMENT_THRESHOLD_MS);

    return () => clearTimeout(engagementTimer);
  }, []);

  return {
    isInstallable,
    isInstalled,
    canShowPrompt,
    isDismissed,
    isEngaged,
    promptInstall,
    dismissPrompt,
  };
};
